import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { subscribeToPendingTicketsByBarber, QueueEstimation } from '@/services/queueService';
import { Barber, BarberDaySchedule, BarberStatus } from '@/types';
import { pendingBreakMinutes } from '@/lib/breaks';

interface UseQueueEstimationProps {
  barberId: string | null;
  branchId?: string;
  serviceDuration: number;
  barber?: Barber | null;
}

interface UseQueueEstimationReturn {
  estimation: QueueEstimation | null;
  loading: boolean;
  error: string | null;
  isOutsideWorkingHours: boolean;
  workingHoursWarning: string | null;
}


/**
 * Hook to calculate queue estimation and validate barber working hours
 *
 * Sobre el descanso: si el barbero está en un descanso temporal (comiendo,
 * gestión rápida…) sigue aceptando turnos, pero la hora a la que el cliente
 * debe llegar tiene que incluir lo que le queda de descanso. Sin esto, la
 * pantalla de confirmación decía "llega a las 10:22" mientras la tarjeta del
 * barbero, un paso antes, ya sumaba el almuerzo (useBarberWaitTime): el
 * cliente se presentaba y se lo encontraba comiendo.
 */
export function useQueueEstimation({
  barberId,
  branchId,
  serviceDuration,
  barber,
}: UseQueueEstimationProps): UseQueueEstimationReturn {
  const [estimation, setEstimation] = useState<QueueEstimation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOutsideWorkingHours, setIsOutsideWorkingHours] = useState(false);
  const [workingHoursWarning, setWorkingHoursWarning] = useState<string | null>(null);
  const [barberStatus, setBarberStatus] = useState<BarberStatus | null>(null);

  // Suscripción al estado en tiempo real del barbero, para conocer el descanso en curso.
  useEffect(() => {
    if (!barberId || barberId === 'any') {
      setBarberStatus(null);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'barberStatus', barberId),
      (snapshot) => {
        setBarberStatus(snapshot.exists() ? (snapshot.data() as BarberStatus) : null);
      },
      (err) => {
        // Si falla, se degrada al cálculo sin descanso en vez de romper la pantalla.
        console.error('Error subscribing to barberStatus:', err);
        setBarberStatus(null);
      }
    );

    return () => unsubscribe();
  }, [barberId]);

  useEffect(() => {
    if (!barberId || !serviceDuration) {
      console.log('⚠️ useQueueEstimation - Missing barberId or serviceDuration:', { barberId, serviceDuration });
      return;
    }

    // If barberId is "any", we need to handle it differently
    if (barberId === 'any') {
      console.log('⚠️ useQueueEstimation - Barber is "any", showing default estimation');
      setLoading(false);
      const now = new Date();
      setEstimation({
        estimatedWaitMinutes: 20, // 20 minutos para llegar si eres el primero
        estimatedCompletionTime: new Date(now.getTime() + (20 + serviceDuration) * 60000),
        queuePosition: 1,
        totalInQueue: 0,
        // Con "cualquier barbero" no se consulta ninguna cola ni ningún descanso:
        // estos valores son fijos y no salen de ningún dato real.
        remainingBreakMinutes: 0
      });
      return;
    }

    // Subscribe to real-time queue updates
    setLoading(true);
    setError(null);

    const calculateEstimation = (tickets: any[]) => {
      let waitBeforeService = 0;
      const now = Date.now();

      for (const ticket of tickets) {
        if (ticket.status === 'in_service' && ticket.serviceStartedAt) {
          // For tickets in service, calculate REMAINING service time
          const serviceStartTime = ticket.serviceStartedAt.toMillis();
          const elapsedMs = now - serviceStartTime;
          const elapsedMinutes = elapsedMs / 60000;
          const serviceDuration = ticket.serviceDuration || 30;
          const remainingMinutes = Math.max(0, serviceDuration - elapsedMinutes);
          waitBeforeService += remainingMinutes;
        } else if (ticket.status === 'finishing' && ticket.finishingAt) {
          // Ticket is finishing soon - calculate time until finishingAt
          const finishingAtMs = ticket.finishingAt.toMillis();
          const remainingMs = Math.max(0, finishingAtMs - now);
          const remainingMinutes = remainingMs / 60000;
          waitBeforeService += remainingMinutes;
        } else {
          // For waiting/notified/arrived tickets, use only the service duration
          // Do NOT add timerExpiry (courtesy/arrival time) - that only applies to the client themselves
          const ticketDuration = ticket.serviceDuration || 30;
          waitBeforeService += ticketDuration;
        }
      }

      // Quien reserva ahora entra al final de la cola, y la comida está anclada
      // al final de la cola que había al marcarla: siempre va detrás. No hay
      // que comparar minutos, y hacerlo era el defecto: esta estimación es
      // nominal (30 min por turno) mientras el servidor re-ancla la comida con
      // la media histórica (~43), así que «¿mi hueco llega hasta la comida?»
      // daba que no, y la comida desaparecía de la pantalla de reserva hasta
      // que ya había empezado. Aviso del negocio del 7-09-2026.
      const descanso = pendingBreakMinutes(barberStatus);

      // Sin nadie delante, 20 minutos de cortesía para llegar; si además el
      // barbero está parando, manda su hora de vuelta.
      const finalWaitMinutes =
        tickets.length === 0
          ? Math.max(20, descanso)
          : Math.ceil(waitBeforeService + descanso);

      // Time when your service will be completed (wait + your service duration)
      const totalTimeUntilCompletion = finalWaitMinutes + serviceDuration;

      // Calculate estimated completion time
      const nowDate = new Date();
      const estimatedCompletionTime = new Date(nowDate.getTime() + totalTimeUntilCompletion * 60000);

      const est: QueueEstimation = {
        estimatedWaitMinutes: finalWaitMinutes,
        estimatedCompletionTime,
        queuePosition: tickets.length + 1,
        totalInQueue: tickets.length,
        remainingBreakMinutes: descanso
      };

      setEstimation(est);
      setLoading(false);

      // Validate against barber working hours if barber info is available
      if (barber) {
        const validation = validateWorkingHours(est.estimatedCompletionTime, barber);
        setIsOutsideWorkingHours(!validation.isValid);
        setWorkingHoursWarning(validation.warning);
      }
    };

    console.log('🔄 useQueueEstimation - Subscribing to real-time updates for barber:', barberId, 'branch:', branchId);

    const unsubscribe = subscribeToPendingTicketsByBarber(
      barberId,
      branchId,
      calculateEstimation,
      (err) => {
        console.error('❌ useQueueEstimation - Subscription error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => {
      console.log('🔄 useQueueEstimation - Unsubscribing from barber:', barberId);
      unsubscribe();
    };
  }, [barberId, branchId, serviceDuration, barber, barberStatus]);

  return {
    estimation,
    loading,
    error,
    isOutsideWorkingHours,
    workingHoursWarning,
  };
}

/**
 * Validate if estimated completion time is within barber's working hours
 */
function validateWorkingHours(
  estimatedTime: Date,
  barber: Barber
): { isValid: boolean; warning: string | null } {
  // Get day of week (0 = Sunday, 1 = Monday, etc.)
  const dayOfWeek = estimatedTime.getDay();
  const dayNames: Array<keyof typeof barber.schedule> = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];

  // Spanish day names for display
  const dayNamesSpanish: Record<string, string> = {
    sunday: 'Domingo',
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
  };

  const dayName = dayNames[dayOfWeek];
  const dayNameSpanish = dayNamesSpanish[dayName];
  const daySchedule: BarberDaySchedule = barber.schedule[dayName];

  // Check if barber is working today
  if (!daySchedule.isWorking) {
    return {
      isValid: false,
      warning: `El barbero no trabaja los ${dayNameSpanish}`,
    };
  }

  // Check if estimated time is within working hours
  if (daySchedule.workEnd) {
    const [endHour, endMinute] = daySchedule.workEnd.split(':').map(Number);
    const workEndTime = new Date(estimatedTime);
    workEndTime.setHours(endHour, endMinute, 0, 0);

    if (estimatedTime > workEndTime) {
      return {
        isValid: false,
        warning: `La hora estimada (${estimatedTime.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit',
        })}) supera el horario de cierre del barbero (${daySchedule.workEnd})`,
      };
    }
  }

  return {
    isValid: true,
    warning: null,
  };
}
